import {useTranslation} from 'react-i18next';
import Forms from '../../../components/Forms';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import ReactQuill from 'react-quill';
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {slugify} from "../../../utils/tools";

/**
 * Props:
 * - post?: { _id, title, text, publishedAt, active, slug? }
 * - onPostsUpdate: () => void
 * - close: () => void
 */
const BlogModal = ({post, close, onPostsUpdate}) => {
    const {t} = useTranslation();
    const [active, setActive] = useState(true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    const [title, setTitle] = useState('');
    const [slug, setSlug] = useState('');
    const [slugDirty, setSlugDirty] = useState(false);
    const [text, setText] = useState(''); // HTML
    const [publishedAt, setPublishedAt] = useState('');

    const quillRef = useRef(null);
    const fileInputRef = useRef(null);

    // init state
    useEffect(() => {
        if (post) {
            setTitle(post.title || '');
            setText(post.text || '');

            // slug z existujícího záznamu
            const s = post.slug || '';
            setSlug(s);
            setSlugDirty(Boolean(s)); // pokud má slug, považujeme ho za ručně nastavený

            // převedeme ISO na datetime-local
            const iso = post.publishedAt ? new Date(post.publishedAt) : new Date();
            const local = new Date(iso.getTime() - (iso.getTimezoneOffset() * 60000))
                .toISOString()
                .slice(0, 16);
            setPublishedAt(local);
            setActive(Boolean(post.active));
        } else {
            // defaulty pro nový
            const now = new Date();
            const local = new Date(now.getTime() - (now.getTimezoneOffset() * 60000))
                .toISOString()
                .slice(0, 16);
            setPublishedAt(local);
            setActive(true);
            setTitle('');
            setText('');
            setSlug('');        // nový: slug prázdný
            setSlugDirty(false); // a negenerovalo se, dokud nezačne psát title
        }
        setErr(null);
    }, [post]);

    // Auto-generování slugu z title, pokud nebyl ručně měněn
    useEffect(() => {
        if (!slugDirty && title) {
            setSlug(slugify(title));
        }
    }, [title, slugDirty]);

    const toolbarModules = useMemo(() => ({
        toolbar: {
            container: [
                [{'header': [1, 2, 3, false]}],
                ['bold', 'italic', 'underline', 'strike'],
                [{'list': 'ordered'}, {'list': 'bullet'}],
                ['link', 'image'],
                ['clean']
            ],
            handlers: {
                image: () => {
                    fileInputRef.current?.click();
                }
            }
        }
    }), []);

    const onImageFileChosen = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const fd = new FormData();
            fd.append('image', file);
            const {data} = await API.post(API_ENDPOINTS.blog.uploadImage, fd, {
                headers: {'Content-Type': 'multipart/form-data'}
            });
            const url = data?.url;
            if (!url) throw new Error('Chybí URL z uploadu');

            const quill = quillRef.current?.getEditor?.();
            if (quill) {
                const range = quill.getSelection(true);
                quill.insertEmbed(range ? range.index : 0, 'image', url, 'user');
            } else {
                setText(prev => `${prev}<p><img src="${url}" alt="" /></p>`);
            }
        } catch (e1) {
            console.error('Upload image failed:', e1);
            setErr(e1?.response?.data?.message || e1.message || 'Upload obrázku selhal.');
        } finally {
            e.target.value = '';
        }
    };

    const validate = () => {
        if (!title.trim()) return t('validation.required', {field: t('blog.title')});
        if (!text || !text.trim()) return t('validation.required', {field: t('blog.text')});
        if (!publishedAt) return t('validation.required', {field: t('blog.publishedAt')});
        if (slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug))
            return t('validation.invalidSlug');
        return null;
    };

    const onSave = useCallback(async () => {
        setErr(null);
        const v = validate();
        if (v) {
            setErr(v);
            return;
        }

        setSaving(true);
        try {
            // převod z datetime-local na ISO
            const publishedIso = new Date(publishedAt).toISOString();

            const payload = {
                title: title.trim(),
                text, // HTML
                publishedAt: publishedIso,
                active: Boolean(active),
                ...(slug ? {slug: slug.trim()} : {}) // slug posílej jen pokud je vyplněný
            };

            if (post?._id) {
                await API.put(API_ENDPOINTS.blog.update(post._id), payload);
            } else {
                await API.post(API_ENDPOINTS.blog.create, payload);
            }

            await onPostsUpdate?.();
            close();
        } catch (e) {
            console.error('Save blog post failed:', e);
            setErr(e?.response?.data?.message || e.message || t('saveFailed'));
        } finally {
            setSaving(false);
        }
    }, [post?._id, title, text, publishedAt, active, slug, onPostsUpdate, close, t]);

    // handler pro ruční změnu slugu
    const handleSlugChange = (val) => {
        setSlug(val);
        setSlugDirty(val.length > 0 ? true : false);
    };

    return (
        <div className="flex flex-col gap-4">
            {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}

            <Forms.Text
                label={t('blog.title')}
                value={title}
                onChange={setTitle}
                required
            />

            {/* slug */}
            <Forms.Text
                label={t('blog.slug')}
                placeholder={t('blog.slug')}
                value={slug}
                onChange={handleSlugChange}
                // pokud máš ve Forms.Text prop "error", můžeš ji použít:
                // error={slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ? t('validation.invalidSlug') : undefined}
            />

            {/* datetime-local */}
            <div className="flex flex-col gap-1">
                <label className="font-medium">{t('blog.publishedAt')} *</label>
                <input
                    type="datetime-local"
                    value={publishedAt}
                    onChange={(e) => setPublishedAt(e.target.value)}
                    className="border rounded px-2 py-1"
                />
            </div>

            <label className="inline-flex items-center gap-2">
                <input
                    type="checkbox"
                    className="cursor-pointer"
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                />
                <span>{t('blog.active')}</span>
            </label>

            {/* WYSIWYG editor */}
            <div className="flex flex-col gap-1">
                <label className="font-medium">{t('blog.text')} *</label>
                <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={text}
                    onChange={setText}
                    modules={toolbarModules}
                />
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onImageFileChosen}
                />
            </div>

            <div className="flex justify-end gap-8 mt-2">
                <Forms.Button label={post?._id ? t('save') : t('create')} onClick={onSave} disabled={saving}/>
            </div>
        </div>
    );
};

export default BlogModal;
